import { NavLink, Link } from "react-router-dom";

import Icon from "../../../a-atoms/icons/Icon";
import Button from "../../../b-molecules/buttons/Button";
import style from "./Rail.module.css";
import logo from "../../../../logo.svg";
import { useEffect, useRef, useState, useMemo } from "react";
import { getAuth, signOut } from "firebase/auth";
import { useSelector } from "react-redux";
import Dialog from "../../dialogs/Dialog";
import Input from "../../../b-molecules/formElements/input/Input";
import { child, get, onValue, ref, update } from "firebase/database";
import orgSlice from "../../../../features/orgSlice";
import { db } from "../../../../configs/Firebase";
import Dropdown from "../../dialogs/Dropdown";
import Select from "../../../b-molecules/formElements/select/Select";
import { useTimeMachine } from "../../../../contexts/TimeMachineContext";
import Checkbox from "../../../b-molecules/formElements/checkbox/Checkbox";
import Fuse from "fuse.js";
import NotificationBadge from "../../../b-molecules/notifications/NotificationBadge";
import { 
  canViewSection, 
  canManageAccess, 
  getAllSectionPermissions,
  SECTIONS 
} from "../../../../utils/permissions";

const UserPermissionsRow = ({ user, selectedOrgId, onChangePermissions, onCopyLink }) => {
  const [isAdmin, setIsAdmin] = useState(user.role === "admin");
  const [permissions, setPermissions] = useState(() => {
    if (user.role === "admin") {
      return { journeys: "write", customers: "write", personas: "write", insights: "write" };
    }
    if (user.permissions) {
      return { ...user.permissions };
    }
    if (user.role === "write") {
      return { journeys: "write", customers: "write", personas: "write", insights: "write" };
    }
    return { journeys: "view", customers: "view", personas: "view", insights: "view" };
  });

  useEffect(() => {
    if (user.role === "admin") {
      setIsAdmin(true);
      setPermissions({ journeys: "write", customers: "write", personas: "write", insights: "write" });
    } else if (user.permissions) {
      setIsAdmin(false);
      setPermissions({ ...user.permissions });
    } else if (user.role === "write") {
      setIsAdmin(false);
      setPermissions({ journeys: "write", customers: "write", personas: "write", insights: "write" });
    } else {
      setIsAdmin(false);
      setPermissions({ journeys: "view", customers: "view", personas: "view", insights: "view" });
    }
  }, [user]);

  const handleAdminChange = (e) => {
    const newIsAdmin = e.target.checked;
    setIsAdmin(newIsAdmin);
    if (newIsAdmin) {
      onChangePermissions(true, { journeys: "write", customers: "write", personas: "write", insights: "write" });
    } else {
      onChangePermissions(false, permissions);
    }
  };

  const handleSectionPermissionChange = (section, permission) => {
    if (isAdmin) return; // Admin can't change individual sections
    
    const newPermissions = { ...permissions, [section]: permission };
    setPermissions(newPermissions);
    onChangePermissions(false, newPermissions);
  };

  const sectionOptions = [
    { value: "No Access" },
    { value: "View" },
    { value: "Write" },
  ];

  const getSelectedIndex = (section) => {
    const perm = permissions[section];
    if (!perm || perm === null) return 0;
    if (perm === "view") return 1;
    if (perm === "write") return 2;
    return 0;
  };

  if (!user.id) {
    // Pending user - show email only
    return (
      <div className={style.active}>
        <div className={style.details}>
          <span>{user.email}</span>
          <span>Pending</span>
        </div>
        <div className={style.actions}>
          <Icon onClick={onCopyLink}>content_copy</Icon>
        </div>
      </div>
    );
  }

  return (
    <div className={style.active}>
      <div className={style.details}>
        <span>{user.email}</span>
        <span>Active</span>
      </div>
      <div className={style.permissionsControls}>
        <div className={style.adminControl}>
          <Checkbox
            checked={isAdmin}
            onChange={handleAdminChange}
            value="Admin"
          />
        </div>
        {!isAdmin && (
          <div className={style.sectionControls}>
            <div className={style.sectionControl}>
              <Select
                label="Journeys"
                multiselect={false}
                type="dropdown"
                variants={["hideError"]}
                options={sectionOptions}
                onChange={(e, index) => {
                  const perm = index === 0 ? null : index === 1 ? "view" : "write";
                  handleSectionPermissionChange("journeys", perm);
                }}
                selectedOptions={[sectionOptions[getSelectedIndex("journeys")].value]}
              />
            </div>
            <div className={style.sectionControl}>
              <Select
                label="Customers"
                multiselect={false}
                type="dropdown"
                variants={["hideError"]}
                options={sectionOptions}
                onChange={(e, index) => {
                  const perm = index === 0 ? null : index === 1 ? "view" : "write";
                  handleSectionPermissionChange("customers", perm);
                }}
                selectedOptions={[sectionOptions[getSelectedIndex("customers")].value]}
              />
            </div>
            <div className={style.sectionControl}>
              <Select
                label="Personas"
                multiselect={false}
                type="dropdown"
                variants={["hideError"]}
                options={sectionOptions}
                onChange={(e, index) => {
                  const perm = index === 0 ? null : index === 1 ? "view" : "write";
                  handleSectionPermissionChange("personas", perm);
                }}
                selectedOptions={[sectionOptions[getSelectedIndex("personas")].value]}
              />
            </div>
            <div className={style.sectionControl}>
              <Select
                label="Insights"
                multiselect={false}
                type="dropdown"
                variants={["hideError"]}
                options={sectionOptions}
                onChange={(e, index) => {
                  const perm = index === 0 ? null : index === 1 ? "view" : "write";
                  handleSectionPermissionChange("insights", perm);
                }}
                selectedOptions={[sectionOptions[getSelectedIndex("insights")].value]}
              />
            </div>
          </div>
        )}
        <Icon onClick={onCopyLink}>content_copy</Icon>
      </div>
    </div>
  );
};

const Rail = (props) => {
  const { timeMachineMode } = useTimeMachine();
  const [showAccount, setShowAccount] = useState(false);
  const auth = getAuth();
  const [selectedOrg, setSelectedOrg] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const orgsStatus = useSelector((state) => state.orgsStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const [showUsers, setShowUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (userStatus) {
      try {
        setCurrentUser(JSON.parse(userStatus.payload));
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, [userStatus]);

  useEffect(() => {
    if (orgsStatus && currentUser) {
      for (let i in orgsStatus.payload) {
        if (orgsStatus.payload[i].selected) {
          const orgId = orgsStatus.payload[i].id;
          setIsAdmin(canManageAccess(currentUser, orgId));
          setSelectedOrg(orgsStatus.payload[i]);
        }
      }
    }
  }, [orgsStatus, currentUser]);

  useEffect(() => {
    if (selectedOrg.id) {
      onValue(ref(db, "userData/" + selectedOrg.id + "/users/"), (snapshot) => {
        let data = snapshot.val();

        let tempUsers = [];
        for (let i in data) {
          if (data[i].id) {
            get(child(ref(db), "userData/" + data[i].id + "/linked/" + selectedOrg.id)).then((snapshot2) => {
              const linkedData = snapshot2.val();
              tempUsers.push({ 
                ...data[i], 
                role: linkedData?.role || "read",
                permissions: linkedData?.permissions || null
              });

              onValue(ref(db, "userData/" + data[i].id + "/linked/" + selectedOrg.id), (snapshot3) => {
                const updatedLinkedData = snapshot3.val();
                const userIndex = tempUsers.findIndex((user) => user.id === data[i].id);
                if (userIndex !== -1) {
                  tempUsers[userIndex].role = updatedLinkedData?.role || "read";
                  tempUsers[userIndex].permissions = updatedLinkedData?.permissions || null;
                  setUsers([...tempUsers]);
                }
              });
            });
          } else {
            tempUsers.push({ ...data[i], role: "read", permissions: null });
          }
        }
        setUsers(tempUsers);
      });
    }
  }, [selectedOrg]);

  // useEffect(() => {
  //   console.log(users);
  // },[users]);

  const [copied, setCopied] = useState(false);
  function copyAccessLink() {
    let tempLink =
      window.location.protocol +
      "//" +
      window.location.host +
      "/access/" +
      btoa(selectedOrg.id);
    navigator.clipboard.writeText(tempLink);
    setCopied(true);
    console.log(tempLink);
  }

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 4000);
    }
  }, [copied]);

  function showUsersHandler() {
    setShowUsers(true);
  }
  function hideUsersHandler() {
    setShowUsers(false);
    // Clear search and input when closing dialog
    setNewUserValue("");
    setSearchTerm("");
    setError("");
  }

  function showAccountHandler() {
    setShowAccount(true);
  }

  function SignOut() {
    signOut(auth)
      .then(() => { })
      .catch((error) => { });
  }

  const [newUserValue, setNewUserValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState();
  
  // Create Fuse instance for searching users (memoized to avoid recreating on every render)
  const fuse = useMemo(() => {
    return new Fuse(users, {
      keys: [{ name: "email" }],
      includeMatches: true,
      threshold: 0.3,
    });
  }, [users]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm) {
      return users;
    }
    return fuse.search(searchTerm).map(result => result.item);
  }, [searchTerm, fuse, users]);

  function onChangeHandler(e) {
    const value = e.target.value;
    setNewUserValue(value);
    setSearchTerm(value);
    setError(""); // Clear error when user types
  }

  function onAddUserHandler() {
    if (validateEmail(newUserValue)) {
      setError("");
      let tempEmail = newUserValue.replace(/[.@]/g, "-");
      update(ref(db, "userData/" + selectedOrg.id + "/users/" + tempEmail), {
        email: newUserValue,
      });
      // Clear input after adding user
      setNewUserValue("");
      setSearchTerm("");
    } else {
      setError("Please ensure the email address is valid");
    }
  }

  const validateEmail = (email) => {
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    return regex.test(email);
  };

  function changePermissions(uid, isAdminUser, sectionPermissions) {
    let userDataRef = ref(db, "userData/" + uid + "/linked/" + selectedOrg.id);
    
    if (isAdminUser) {
      // If admin, set role to "admin" and remove permissions (admin takes precedence)
      // Setting permissions to null will remove the field
      update(userDataRef, { 
        role: "admin",
        permissions: null
      });
    } else {
      // If not admin, set permissions object and set role based on permissions
      const hasAllWrite = Object.values(sectionPermissions).every(p => p === "write");
      const hasAllView = Object.values(sectionPermissions).every(p => p === "view" || p === null);
      const hasAnyNull = Object.values(sectionPermissions).some(p => p === null);
      
      // If all sections have same permission level, use legacy role for backward compatibility
      if (hasAllWrite) {
        // All write - use legacy role and remove permissions
        update(userDataRef, { 
          role: "write",
          permissions: null
        });
      } else if (hasAllView && !hasAnyNull) {
        // All view - use legacy role and remove permissions
        update(userDataRef, { 
          role: "read",
          permissions: null
        });
      } else {
        // Mixed permissions or some sections with no access - use new granular structure
        // Don't set role field - let permissions take precedence
        update(userDataRef, { 
          permissions: sectionPermissions
        });
      }
    }
  }

  return (
    <>
      <div className={`${style.rail} ${timeMachineMode ? style.timeMachineMode : ''}`}>
        <div className={style.menu}>
          <Link to="/">
            <div className={style.logo}>
              <img src={logo} width="88" height="88" />
            </div>
          </Link>

          {currentUser && selectedOrg && canViewSection(currentUser, selectedOrg.id, SECTIONS.JOURNEYS) && (
            <div>
              <NavLink to="journeys">
                <Button variants={["text", "stacked", "rail"]}>
                  <Icon>conversion_path</Icon> Journeys
                </Button>
              </NavLink>
            </div>
          )}
          {currentUser && selectedOrg && canViewSection(currentUser, selectedOrg.id, SECTIONS.CUSTOMERS) && (
            <div>
              <NavLink to="customers">
                <Button variants={["text", "stacked", "rail"]}>
                  <Icon>chat</Icon> Customers
                </Button>
              </NavLink>
            </div>
          )}
          {currentUser && selectedOrg && canViewSection(currentUser, selectedOrg.id, SECTIONS.PERSONAS) && (
            <div>
              <NavLink to="personas">
                <Button variants={["text", "stacked", "rail"]}>
                  <Icon>people</Icon> Personas
                </Button>
              </NavLink>
            </div>
          )}
          {currentUser && selectedOrg && canViewSection(currentUser, selectedOrg.id, SECTIONS.INSIGHTS) && (
            <div>
              <NavLink to="insights">
                <Button variants={["text", "stacked", "rail"]}>
                  <Icon>insights</Icon> Insights
                </Button>
              </NavLink>
            </div>
          )}
        </div>
        <div className={style.menu}>
          {props.user && currentUser ? (
            <NotificationBadge />
          ) : null}
          {props.user ? (
            <div className={style.accountButton}>
              <a>
                <Button
                  variants={["text", "stacked"]}
                  onClick={showAccountHandler}
                >
                  <div
                    className={style.profile}
                    style={props.user.profile?.microsoftPhotoURL ? {
                      backgroundImage: `url(${props.user.profile.microsoftPhotoURL})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      color: 'transparent'
                    } : {}}
                  >
                    {!props.user.profile?.microsoftPhotoURL && (
                      <>
                        {props.user.displayName?.split(" ")[0]?.charAt(0) || props.user.email?.charAt(0) || "?"}
                        {props.user.displayName?.split(" ")[1]?.charAt(0) || ""}
                      </>
                    )}
                  </div>
                  {props.user.displayName?.split(" ")[0] || props.user.email || "User"}
                </Button>
              </a>
              <div
                className={`${style.accountOptions} ${showAccount && style.show
                  }`}
              >
                <ul>
                  {currentUser && selectedOrg && canManageAccess(currentUser, selectedOrg.id) && (
                    <li onClick={showUsersHandler}>Manage Users</li>
                  )}
                  <li onClick={SignOut}>Sign Out</li>
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <Dialog
        show={showUsers}
        title={selectedOrg.name + " Users"}
        onClose={hideUsersHandler}
        variants={["large"]}
      >
        <div className={style.userManagementActions}>
          <Input
            label="Search or Add User"
            placeholder="name@company.com"
            onChange={onChangeHandler}
            value={newUserValue}
            error={error}
          />
          <Button 
            preIcon="add" 
            onClick={onAddUserHandler}
            disabled={!validateEmail(newUserValue)}
          ></Button>
        </div>
        <div className={`${style.copySuccess} ${copied && style.show}`}>
          <Icon>check_circle</Icon>
          <span>
            Access link was copied to your clipboard. Please sent the link to
            the user to give them access.
          </span>
        </div>

        <div className={style.users}>
          {filteredUsers.map((user, index) => (
            <UserPermissionsRow
              key={index}
              user={user}
              selectedOrgId={selectedOrg.id}
              onChangePermissions={(isAdmin, permissions) => changePermissions(user.id, isAdmin, permissions)}
              onCopyLink={copyAccessLink}
            />
          ))}
        </div>
      </Dialog>
    </>
  );
};

export default Rail;
